/**
 * MarketingAnalyticsTab — Analytics for marketing: visits, pixel audit
 */
import React, { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from "recharts";
import { Card, CardContent } from "@/components/ui/card";
import DashboardPixelAudit from "@/components/dashboard/DashboardPixelAudit";

const CustomTooltip = ({ active, payload, label, prefix = "" }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-lg px-3 py-2 shadow-lg">
      <p className="text-xs text-gray-500 font-medium">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="text-sm font-bold" style={{ color: p.color }}>
          {p.name}: {prefix}{typeof p.value === "number" ? p.value.toFixed(p.value % 1 ? 2 : 0) : p.value}
        </p>
      ))}
    </div>
  );
};

export default function MarketingAnalyticsTab() {
  const { authAxios } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [pixelAudit, setPixelAudit] = useState(null);
  const [restaurant, setRestaurant] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [analyticsRes, pixelRes, restRes] = await Promise.all([
          authAxios.get("/dashboard/analytics?days=14").catch(() => ({ data: null })),
          authAxios.get("/dashboard/pixel-audit?days=30").catch(() => ({ data: null })),
          authAxios.get("/restaurant").catch(() => ({ data: null })),
        ]);
        setAnalytics(analyticsRes.data);
        setPixelAudit(pixelRes.data);
        setRestaurant(restRes.data);
      } catch {
        toast.error("Error cargando datos de marketing");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [authAxios]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-40 text-muted-foreground text-sm">
        Carregando…
      </div>
    );
  }

  // Data transformations (same as DashboardCharts)
  const visitData = (analytics?.visit_stats || []).map(d => ({ date: d._id?.slice(5) || "", visitas: d.visits }));
  const hourlyVisitData = Array.from({ length: 24 }, (_, i) => {
    const h = String(i).padStart(2, "0");
    const found = (analytics?.hourly_visit_stats || []).find(s => s._id === h);
    return { hour: `${h}h`, visitas: found?.visits || 0 };
  });

  return (
    <div className="space-y-6">
      {/* Pixel Audit Card */}
      <DashboardPixelAudit pixelAudit={pixelAudit} restaurant={restaurant} />

      {/* Visits by day and hour */}
      <div className="grid md:grid-cols-2 gap-6">
        <Card className="bg-card border-border/50">
          <CardContent className="pt-5">
            <h3 className="font-bold uppercase text-sm mb-4">Visitas por dia</h3>
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={visitData}>
                <defs><linearGradient id="gradVisitas" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#6366f1" stopOpacity={0.3} /><stop offset="95%" stopColor="#6366f1" stopOpacity={0} /></linearGradient></defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                <XAxis dataKey="date" tick={{ fill: "#9ca3af", fontSize: 10 }} />
                <YAxis tick={{ fill: "#9ca3af", fontSize: 11 }} allowDecimals={false} />
                <Tooltip content={<CustomTooltip />} />
                <Area type="monotone" dataKey="visitas" name="Visitas" stroke="#6366f1" strokeWidth={2.5} fill="url(#gradVisitas)" dot={{ fill: "#6366f1", r: 3 }} />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
        <Card className="bg-card border-border/50">
          <CardContent className="pt-5">
            <h3 className="font-bold uppercase text-sm mb-4">Visitas por hora <span className="text-xs font-normal text-indigo-400 normal-case">(hoje)</span></h3>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={hourlyVisitData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                <XAxis dataKey="hour" tick={{ fill: "#9ca3af", fontSize: 9 }} />
                <YAxis tick={{ fill: "#9ca3af", fontSize: 11 }} allowDecimals={false} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="visitas" name="Visitas" fill="#6366f1" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
